import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { allergenService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { useToast } from '../hooks/useToast';
import Toast from '../components/common/Toast';

interface Allergen {
  id: number;
  name: string;
  name_en?: string;
  icon: string;
  created_at: string;
  updated_at: string;
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  flex: 1;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 767px) {
    max-width: none;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  
  &:hover {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  &:nth-child(2) { width: 80px; }
  &:nth-child(3) { width: 220px; }
`;

const TableCell = styled.td`
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 16px;
    width: 96%;
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  
  &:hover {
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

// Aggiungo un componente per l'icona di default
const DefaultIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #fbbf24;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  display: inline-block;
`;

const IconDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const IconText = styled.span`
  font-size: 20px;
`;

const EnglishName = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  margin-top: 2px;
`;

const Allergens: React.FC = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'cook';
  const { toasts, showToast } = useToast();
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAllergen, setEditingAllergen] = useState<Allergen | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    icon: '',
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteName, setDeleteName] = useState<string | null>(null);

  // Hook per la traduzione automatica del nome
  const { translatedText: translatedName, isTranslating: isTranslatingName } = useAutoTranslate(
    formData.name,
    formData.name_en,
    1000
  );

  // Aggiorna automaticamente il campo inglese quando è disponibile una traduzione
  useEffect(() => {
    if (translatedName && !formData.name_en.trim()) {
      setFormData(prev => ({ ...prev, name_en: translatedName }));
    }
  }, [translatedName, formData.name_en]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await allergenService.getAll();
        if (!cancelled) setAllergens(response.data.allergens || []);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Errore nel caricamento degli allergeni');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredAllergens = Array.isArray(allergens)
    ? allergens
        .filter(allergen =>
          allergen.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
        )
    : [];

  const renderIcon = (icon: string) => {
    if (!icon || icon.trim() === '') {
      return <DefaultIcon title="Icona predefinita" />;
    }
    
    // Se l'icona è un emoji o testo
    if (icon.length <= 4) {
      return <IconText>{icon}</IconText>;
    }
    
    // Se l'icona è un URL
    if (icon.startsWith('http') || icon.startsWith('/')) {
      return <img src={icon} alt="Icona allergene" style={{ width: '24px', height: '24px' }} />;
    }
    
    // Altrimenti mostra come testo
    return <IconText>{icon}</IconText>;
  };

  const openCreateModal = () => {
    setEditingAllergen(null);
    setFormData({ name: '', name_en: '', icon: '' });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (allergen: Allergen) => {
    setEditingAllergen(allergen);
    setFormData({
      name: allergen.name,
      name_en: allergen.name_en || '',
      icon: allergen.icon || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingAllergen(null);
    setFormData({ name: '', name_en: '', icon: '' });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors: any = {};
    if (!formData.name.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isModalOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const dataToSend = {
        name: formData.name,
        ...(formData.name_en.trim() && { name_en: formData.name_en.trim() }),
        ...(formData.icon.trim() && { icon: formData.icon })
      };

      if (editingAllergen) {
        const res = await allergenService.update(editingAllergen.id, dataToSend);
        setAllergens(prev => prev.map(a => a.id === editingAllergen.id ? res.data.allergen : a));
        showToast('Allergene aggiornato');
      } else {
        const res = await allergenService.create(dataToSend);
        setAllergens(prev => [...prev, res.data.allergen]);
        showToast('Allergene creato');
      }

      closeModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
      showToast('Errore nel salvataggio', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (deleteId == null) return;
    const backup = allergens;
    const idToDelete = deleteId;
    setAllergens(prev => prev.filter(a => a.id !== idToDelete));
    setDeleteId(null);
    setDeleteName(null);
    try {
      await allergenService.delete(idToDelete);
      showToast('Allergene eliminato');
    } catch (err: any) {
      setAllergens(backup);
      setError(err.response?.data?.message || 'Errore nell\'eliminazione');
      showToast('Errore nell\'eliminazione', 'error');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Caricamento allergeni...</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Toast toasts={toasts} />
      <PageHeader>
        <Title>Gestione Allergeni</Title>
        <SearchContainer>
          <Input
            placeholder="Cerca allergeni..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
          {canEdit && (
            <Button onClick={openCreateModal}>
              Nuovo Allergene
            </Button>
          )}
        </SearchContainer>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Card>
        {filteredAllergens.length === 0 ? (
          <EmptyState>
            {searchTerm ? 'Nessun allergene trovato' : 'Nessun allergene presente'}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Nome</TableHeaderCell>
                <TableHeaderCell>Icona</TableHeaderCell>
                <TableHeaderCell>Azioni</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {filteredAllergens.map((allergen) => (
                <TableRow key={allergen.id}>
                  <TableCell>
                    <strong>{allergen.name}</strong>
                    {allergen.name_en && (
                      <EnglishName>{allergen.name_en}</EnglishName>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconDisplay>
                      {renderIcon(allergen.icon)}
                    </IconDisplay>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <ActionButtons>
                        <Button
                          size="small"
                          variant="secondary"
                          onClick={() => openEditModal(allergen)}
                        >
                          Modifica
                        </Button>
                        <Button
                          size="small"
                          variant="danger"
                          onClick={() => {
                            setDeleteId(allergen.id);
                            setDeleteName(allergen.name);
                          }}
                        >
                          Elimina
                        </Button>
                      </ActionButtons>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal isOpen={isModalOpen}>
        <ModalContent translate="no">
          <ModalHeader>
            <ModalTitle>
              {editingAllergen ? 'Modifica Allergene' : 'Nuovo Allergene'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>
          
          <Form onSubmit={handleSubmit}>
            <Input
              label="Nome"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              required
              fullWidth
            />
            
            <Input
              label="Nome (Inglese)"
              value={formData.name_en}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name_en: e.target.value })}
              placeholder={
                isTranslatingName 
                  ? "Traduzione in corso..." 
                  : translatedName 
                    ? `Suggerimento: ${translatedName}` 
                    : "Inserisci traduzione manuale o lascia vuoto per traduzione automatica"
              }
              fullWidth
            />
            
            <Input
              label="Icona (opzionale)"
              placeholder="Inserisci emoji, testo o URL dell'icona (se vuoto: triangolo giallo)"
              value={formData.icon}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, icon: e.target.value })}
              error={formErrors.icon}
              fullWidth
            />
            
            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={submitting}
                disabled={submitting}
              >
                {editingAllergen ? 'Aggiorna' : 'Crea'}
              </Button>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
      <ConfirmDialog
        isOpen={deleteId != null}
        title="Elimina allergene"
        message={
          deleteName
            ? `Sei sicuro di voler eliminare l'allergene "${deleteName}"?`
            : 'Sei sicuro di voler eliminare questo allergene?'
        }
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => {
          setDeleteId(null);
          setDeleteName(null);
        }}
      />
    </PageContainer>
  );
};

export default Allergens;
